import React, { useCallback, useEffect, useMemo, useState } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { normalizeAdminRichText } from "../../../utils/richText";
import { formatLugaresColombiaDisplay } from "../../../utils/geoDisplayFormat";
import excelIcon from "../../../assets/images/ui/descarga_excel.png";
import bannerImg from "../../../assets/images/ui/banner.png";
import {
  normalizeTipoParam,
  getParamColumns,
  getParamExcelColumnDefs,
  getParamPickerDefs,
  buildInitialPickerState,
  buildResetPickerState,
  buildTableColumnsFromPicker,
  saveParamPickerState,
  MAX_PARAM_VISIBLE_COLUMNS,
} from "./paramEruptivosColumns";
import EvtParametrosTipoTabs from "./EvtParametrosTipoTabs";
import {
  getParamTipoNormFromLabel,
  getParamTiposConDatos,
  paramBelongsToTipo,
} from "./parametrosTipoConfig";
import EvtImagenesGaleriaModal from "./EvtImagenesGaleriaModal";
import { EVENTO_EMPTY_MESSAGES } from "./eventoDetalleConstants";
import "./TablaParametrosEruptivos.css";

function paramColLayoutClass(c) {
  if (!c?.key) return "";
  if (c.key === "__fuente") return "evt-col-fuente";
  if (c.key === "__reporte") return "evt-col-reporte";
  if (c.key === "__imagenes") return "evt-col-img";
  if (c.key === "__index") return "";
  return "evt-col-metric";
}

export default function TablaParametrosEruptivos({
  rows,
  volcanTitle,
  eventoTitle,
}) {
  const tiposDisponibles = useMemo(() => getParamTiposConDatos(rows), [rows]);

  const [tipoSel, setTipoSel] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState([]);
  const [modalIndex, setModalIndex] = useState(0);
  const [excelBusy, setExcelBusy] = useState(false);
  const colPickerFieldId = "evt-param-col-picker";

  useEffect(() => {
    setTipoSel((prev) => {
      if (tiposDisponibles.some((item) => item.label === prev)) return prev;
      return tiposDisponibles[0]?.label ?? null;
    });
  }, [tiposDisponibles]);

  const tipoNorm = getParamTipoNormFromLabel(tipoSel);

  const [colPanelOpen, setColPanelOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(min-width: 1025px)").matches;
  });
  const [colPanelMobile, setColPanelMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 1200px)").matches;
  });
  const [pickerOrder, setPickerOrder] = useState([]);
  const [pickerVisible, setPickerVisible] = useState({});
  const [pickerHint, setPickerHint] = useState("");
  const [dragKey, setDragKey] = useState(null);

  useEffect(() => {
    if (!tipoNorm) return;
    const next = buildInitialPickerState(tipoNorm);
    setPickerOrder(next.order);
    setPickerVisible(next.visible);
    setDragKey(null);
  }, [tipoNorm]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1200px)");
    const sync = () => setColPanelMobile(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const closeColPanel = useCallback(() => setColPanelOpen(false), []);

  useEffect(() => {
    if (!colPanelOpen || !colPanelMobile) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeColPanel();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [colPanelOpen, colPanelMobile, closeColPanel]);

  const persistPicker = useCallback(
    (order, vis) => {
      if (!tipoNorm || !order?.length) return;
      saveParamPickerState(tipoNorm, { order, visible: vis });
    },
    [tipoNorm],
  );

  const cols = useMemo(() => {
    if (!tipoNorm) return [];
    if (!pickerOrder.length) return getParamColumns(tipoNorm);
    return buildTableColumnsFromPicker(tipoNorm, pickerOrder, pickerVisible);
  }, [tipoNorm, pickerOrder, pickerVisible]);

  const visibleColCount = useMemo(() => {
    if (!tipoNorm) return 0;
    if (!pickerOrder.length) return getParamColumns(tipoNorm).length;
    return pickerOrder.filter((k) => pickerVisible[k]).length;
  }, [tipoNorm, pickerOrder, pickerVisible]);

  const pickerDefsByKey = useMemo(() => {
    const m = new Map();
    getParamPickerDefs(tipoNorm).forEach((d) => m.set(d.key, d.label));
    return m;
  }, [tipoNorm]);

  const resetPicker = () => {
    if (!tipoNorm) return;
    const next = buildResetPickerState(tipoNorm);
    setPickerOrder(next.order);
    setPickerVisible(next.visible);
    persistPicker(next.order, next.visible);
    setPickerHint("");
  };

  const setColumnVisible = (key, nextChecked) => {
    if (!nextChecked) {
      const nextVis = { ...pickerVisible, [key]: false };
      const stillOn = pickerOrder.filter((k) => nextVis[k]).length;
      if (stillOn === 0) {
        setPickerHint("Debe quedar al menos una columna visible.");
        window.setTimeout(() => setPickerHint(""), 2200);
        return;
      }
      setPickerVisible(nextVis);
      persistPicker(pickerOrder, nextVis);
      setPickerHint("");
      return;
    }
    if (visibleColCount >= MAX_PARAM_VISIBLE_COLUMNS) {
      setPickerHint(
        `Solo puedes mostrar hasta ${MAX_PARAM_VISIBLE_COLUMNS} columnas a la vez.`,
      );
      window.setTimeout(() => setPickerHint(""), 2600);
      return;
    }
    const nextVis = { ...pickerVisible, [key]: true };
    setPickerVisible(nextVis);
    persistPicker(pickerOrder, nextVis);
    setPickerHint("");
  };

  const reorderPickerKeys = (list, fromKey, beforeKey) => {
    const keys = [...list];
    const fromIdx = keys.indexOf(fromKey);
    let toIdx = beforeKey == null ? keys.length : keys.indexOf(beforeKey);
    if (fromIdx === -1) return list;
    if (toIdx === -1) toIdx = keys.length;
    keys.splice(fromIdx, 1);
    const adjustedTo = fromIdx < toIdx ? toIdx - 1 : toIdx;
    keys.splice(adjustedTo, 0, fromKey);
    return keys;
  };

  const moveKeyBefore = (fromKey, beforeKey) => {
    if (!fromKey || fromKey === beforeKey) return;
    const nextOrder = reorderPickerKeys(pickerOrder, fromKey, beforeKey);
    setPickerOrder(nextOrder);
    persistPicker(nextOrder, pickerVisible);
  };

  const filtered = useMemo(() => {
    if (!tipoSel) return [];
    return (rows || []).filter((p) => paramBelongsToTipo(p, tipoSel));
  }, [rows, tipoSel]);

  const showColPanelAside =
    colPanelOpen && !colPanelMobile && filtered.length > 0;

  useEffect(() => {
    if (filtered.length === 0 && colPanelOpen) {
      setColPanelOpen(false);
    }
  }, [filtered.length, colPanelOpen]);

  const openGallery = (imagenes) => {
    setModalImages(Array.isArray(imagenes) ? imagenes : []);
    setModalIndex(0);
    setModalOpen(true);
  };

  const closeGallery = () => {
    setModalOpen(false);
    setModalImages([]);
    setModalIndex(0);
  };

  const nextImg = () => {
    if (modalImages.length <= 1) return;
    setModalIndex((i) => (i === modalImages.length - 1 ? 0 : i + 1));
  };
  const prevImg = () => {
    if (modalImages.length <= 1) return;
    setModalIndex((i) => (i === 0 ? modalImages.length - 1 : i - 1));
  };

  const shouldRenderRich = (val) => {
    if (val == null) return false;
    const s = String(val);
    if (!s.trim()) return false;
    return s.includes("<") || s.includes("&lt;") || s.includes("\n");
  };

  const colPickerPanelBody = (
    <>
      <div className="evt-param-col-panel-head">
        <div
          className="evt-param-col-panel-title"
          id="evt-param-col-panel-title"
        >
          Personalizar columnas
        </div>
        <button
          type="button"
          className="evt-param-col-panel-close"
          aria-label="Cerrar panel de columnas"
          onClick={closeColPanel}
        >
          <i className="bi bi-x-lg" aria-hidden />
        </button>
      </div>
      <p className="evt-param-col-panel-intro">
        Selecciona las columnas que deseas ver en la tabla y arrástralas para
        reordenarlas.
      </p>
      <p className="evt-param-col-panel-intro evt-param-col-panel-intro--sub">
        Máximo {MAX_PARAM_VISIBLE_COLUMNS} columnas visibles.
      </p>
      <p className="evt-param-col-list-kicker">Columnas visibles</p>
      {pickerHint ? (
        <div className="evt-param-col-panel-hint" role="status">
          {pickerHint}
        </div>
      ) : null}
      <ul className="evt-param-col-list evt-param-col-list--classic">
        {pickerOrder.map((key) => {
          const label = pickerDefsByKey.get(key) ?? key;
          const checked = Boolean(pickerVisible[key]);
          const disableCheck =
            !checked && visibleColCount >= MAX_PARAM_VISIBLE_COLUMNS;
          return (
            <li
              key={key}
              className={`evt-param-col-row${dragKey === key ? " is-dragging" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
              }}
              onDrop={() => {
                if (dragKey) moveKeyBefore(dragKey, key);
                setDragKey(null);
              }}
            >
              <span
                className="evt-param-col-drag-dots"
                data-sgc-tooltip="Arrastrar para reordenar"
                aria-label={`Reordenar: ${label}`}
                draggable
                onDragStart={(e) => {
                  e.stopPropagation();
                  setDragKey(key);
                  try {
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", key);
                  } catch {
                    // ignore
                  }
                }}
                onDragEnd={() => setDragKey(null)}
              >
                <span className="evt-param-col-drag-dots__dot" aria-hidden />
                <span className="evt-param-col-drag-dots__dot" aria-hidden />
                <span className="evt-param-col-drag-dots__dot" aria-hidden />
                <span className="evt-param-col-drag-dots__dot" aria-hidden />
                <span className="evt-param-col-drag-dots__dot" aria-hidden />
                <span className="evt-param-col-drag-dots__dot" aria-hidden />
              </span>
              <label
                className="evt-param-col-row-label"
                htmlFor={`${colPickerFieldId}-${key}`}
              >
                <input
                  id={`${colPickerFieldId}-${key}`}
                  className="evt-param-col-sq-checkbox"
                  type="checkbox"
                  checked={checked}
                  disabled={disableCheck}
                  onChange={(e) => setColumnVisible(key, e.target.checked)}
                />
                <span className="evt-param-col-row-text">{label}</span>
              </label>
              <button
                type="button"
                className="evt-param-col-row-clear"
                aria-label={`Ocultar columna ${label}`}
                data-sgc-tooltip="Ocultar columna"
                disabled={!checked || visibleColCount <= 1}
                onClick={() => setColumnVisible(key, false)}
              >
                <i className="bi bi-x-lg" aria-hidden />
              </button>
            </li>
          );
        })}
        <li
          className="evt-param-col-drop-slot"
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => {
            if (dragKey) moveKeyBefore(dragKey, null);
            setDragKey(null);
          }}
        >
          <span className="evt-param-col-drop-slot-label">
            Soltar aquí para mover al final
          </span>
        </li>
      </ul>
      <div className="evt-param-col-panel-footer evt-param-col-panel-footer--split">
        <button
          type="button"
          className="evt-param-col-reset"
          onClick={resetPicker}
        >
          <i className="bi bi-arrow-clockwise" aria-hidden />
          <span>Restablecer</span>
        </button>
        <button
          type="button"
          className="evt-param-col-apply"
          onClick={closeColPanel}
        >
          Aplicar
        </button>
      </div>
    </>
  );

  if (!rows || rows.length === 0) {
    return (
      <section className="evt-card evt-card--table-embed evt-param-table-card">
        <h4 className="evt-card-title">Parámetros eruptivos</h4>
        <div className="evt-empty">{EVENTO_EMPTY_MESSAGES.parametros}</div>
      </section>
    );
  }

  return (
    <>
      <div
        className={`evt-param-outer-layout${
          showColPanelAside ? " is-col-panel-open" : ""
        }`}
      >
        <section className="evt-card evt-card--table-embed evt-param-table-card">
          <h4 className="evt-card-title">Parámetros eruptivos</h4>

          <div className="evt-param-hero-row">
            {tiposDisponibles.length > 0 ? (
              <div
                className={`evt-efectos-tabs-card evt-param-tabs-card${
                  tiposDisponibles.length === 1 ? " is-single-tab" : ""
                }`}
              >
                <EvtParametrosTipoTabs
                  items={tiposDisponibles}
                  value={tipoSel}
                  onChange={setTipoSel}
                />
              </div>
            ) : null}

            <div className="evt-param-filebox">
              <div className="evt-param-xlsx-card">
                <button
                  type="button"
                  id="evt-param-excel-trigger"
                  className="evt-param-xlsx-card__icon-btn evt-excel-btn"
                  data-sgc-tooltip="Descargar Excel"
                  aria-label="Descargar Excel"
                  disabled={excelBusy || (rows || []).length === 0}
                  onClick={async () => {
                    if (excelBusy || (rows || []).length === 0) return;

                    const htmlToPlainText = (value) => {
                      if (value == null) return "";
                      const s = String(value);
                      if (!s.trim()) return "";
                      if (!/[<>]/.test(s)) return s.trim();
                      try {
                        const doc = new DOMParser().parseFromString(
                          s,
                          "text/html",
                        );
                        return String(doc?.body?.textContent ?? "")
                          .replace(/\s+/g, " ")
                          .trim();
                      } catch {
                        return s
                          .replace(/<[^>]*>/g, " ")
                          .replace(/\s+/g, " ")
                          .trim();
                      }
                    };

                    const safeSheetName = (name, used) => {
                      const base =
                        String(name ?? "")
                          .trim()
                          .replace(new RegExp("[\\[\\]*/\\\\?:]", "g"), " ")
                          .replace(/\s+/g, " ")
                          .trim()
                          .slice(0, 31) || "Parámetros";
                      if (!used.has(base)) {
                        used.add(base);
                        return base;
                      }
                      for (let i = 2; i < 100; i += 1) {
                        const suffix = ` (${i})`;
                        const candidate = (
                          base.slice(0, 31 - suffix.length) + suffix
                        ).slice(0, 31);
                        if (!used.has(candidate)) {
                          used.add(candidate);
                          return candidate;
                        }
                      }
                      return base.slice(0, 31);
                    };

                    const upper = (s) => String(s ?? "").toUpperCase();

                    const buildWorkbook = async () => {
                      const wb = new ExcelJS.Workbook();
                      wb.creator = "Servicio Geológico Colombiano";
                      wb.created = new Date();

                      const bannerBuf = await fetch(bannerImg).then((r) =>
                        r.arrayBuffer(),
                      );
                      const bannerId = wb.addImage({
                        buffer: bannerBuf,
                        extension: "png",
                      });

                      const byTipo = new Map();
                      (rows || []).forEach((p) => {
                        const t =
                          String(p?.tipo_producto_eruptivo ?? "—").trim() ||
                          "—";
                        if (!byTipo.has(t)) byTipo.set(t, []);
                        byTipo.get(t).push(p);
                      });

                      const usedNames = new Set();
                      const tiposLocal = Array.from(byTipo.keys()).sort(
                        (a, b) => a.localeCompare(b, "es"),
                      );

                      tiposLocal.forEach((tipo) => {
                        const tipoNormLocal = normalizeTipoParam(tipo);
                        const excelCols =
                          getParamExcelColumnDefs(tipoNormLocal);

                        const sheetCols = [
                          { key: "__id", label: "ID", align: "center" },
                          ...excelCols,
                        ];

                        const sheetName = safeSheetName(tipo, usedNames);
                        const ws = wb.addWorksheet(sheetName, {
                          properties: { defaultRowHeight: 18 },
                          views: [{ state: "frozen", ySplit: 7 }],
                        });

                        sheetCols.forEach((c, idx) => {
                          const col = ws.getColumn(idx + 1);
                          if (c.key === "__id") col.width = 10;
                          else if (c.key === "__fuente") col.width = 26;
                          else if (c.key === "__reporte") col.width = 34;
                          else if (c.align === "justify") col.width = 38;
                          else col.width = 20;
                        });

                        ws.getRow(1).height = 26;
                        ws.getRow(2).height = 26;
                        ws.getRow(3).height = 26;
                        ws.addImage(bannerId, {
                          tl: { col: 0, row: 0 },
                          br: { col: sheetCols.length, row: 3 },
                          editAs: "oneCell",
                        });

                        const lastColLetter = ws.getColumn(
                          sheetCols.length,
                        ).letter;
                        ws.mergeCells(`A4:${lastColLetter}4`);
                        ws.getCell("A4").value =
                          `Reporte parámetros eruptivos (Volcán: ${
                            String(volcanTitle ?? "").trim() || "—"
                          } | Evento: ${String(eventoTitle ?? "").trim() || "—"})`;
                        ws.getCell("A4").font = {
                          bold: true,
                          color: { argb: "FF1E4D2B" },
                          size: 11,
                        };
                        ws.getCell("A4").alignment = {
                          vertical: "middle",
                          horizontal: "center",
                        };
                        ws.getRow(4).height = 20;

                        ws.mergeCells(`A5:${lastColLetter}5`);
                        ws.getCell("A5").value =
                          `Tipo de producto eruptivo: ${tipo}`;
                        ws.getCell("A5").font = {
                          bold: true,
                          color: { argb: "FF005833" },
                          size: 10,
                        };
                        ws.getCell("A5").alignment = {
                          vertical: "middle",
                          horizontal: "center",
                        };
                        ws.getRow(5).height = 18;

                        ws.mergeCells(`A6:${lastColLetter}6`);
                        ws.getCell("A6").value =
                          `Generado: ${new Date().toLocaleString("es-CO")}`;
                        ws.getCell("A6").font = {
                          color: { argb: "FF646464" },
                          size: 10,
                        };
                        ws.getCell("A6").alignment = {
                          vertical: "middle",
                          horizontal: "center",
                        };
                        ws.getRow(6).height = 18;

                        const headerRow = ws.addRow(
                          sheetCols.map((c) => upper(c.label)),
                        );
                        headerRow.font = {
                          bold: true,
                          color: { argb: "FFFFFFFF" },
                        };
                        headerRow.alignment = {
                          vertical: "middle",
                          horizontal: "center",
                          wrapText: true,
                        };
                        headerRow.height = 28;
                        headerRow.eachCell((cell) => {
                          cell.fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "FF005833" },
                          };
                          cell.border = {
                            top: {
                              style: "thin",
                              color: { argb: "FFBFC9C2" },
                            },
                            left: {
                              style: "thin",
                              color: { argb: "FFBFC9C2" },
                            },
                            bottom: {
                              style: "thin",
                              color: { argb: "FFBFC9C2" },
                            },
                            right: {
                              style: "thin",
                              color: { argb: "FFBFC9C2" },
                            },
                          };
                        });

                        const list = byTipo.get(tipo) || [];
                        list.forEach((p) => {
                          const detalle = p?.detalle ?? {};
                          const values = sheetCols.map((c) => {
                            if (c.key === "__id") return p?.id ?? "—";
                            if (c.key === "__fuente")
                              return (
                                String(
                                  p?.referencia_bibliografica ?? "—",
                                ).trim() || "—"
                              );
                            if (c.key === "__reporte") {
                              const rep = detalle?.reporte_bibliografico ?? "";
                              return htmlToPlainText(rep) || "—";
                            }
                            const raw = detalle?.[c.key];
                            if (c.key === "municipios_afectados") {
                              return (
                                formatLugaresColombiaDisplay(raw) ||
                                htmlToPlainText(raw) ||
                                "—"
                              );
                            }
                            return (
                              htmlToPlainText(raw) ||
                              String(raw ?? "").trim() ||
                              "—"
                            );
                          });

                          const r = ws.addRow(values);
                          r.alignment = { vertical: "top", wrapText: true };
                          r.eachCell((cell, colNumber) => {
                            const k = sheetCols[colNumber - 1]?.key;
                            const align =
                              k === "__id"
                                ? "center"
                                : sheetCols[colNumber - 1]?.align === "center"
                                  ? "center"
                                  : "left";
                            cell.alignment = {
                              vertical: "top",
                              horizontal: align,
                              wrapText: true,
                            };
                            cell.border = {
                              top: {
                                style: "thin",
                                color: { argb: "FFE0E4E5" },
                              },
                              left: {
                                style: "thin",
                                color: { argb: "FFE0E4E5" },
                              },
                              bottom: {
                                style: "thin",
                                color: { argb: "FFE0E4E5" },
                              },
                              right: {
                                style: "thin",
                                color: { argb: "FFE0E4E5" },
                              },
                            };
                          });
                        });
                      });

                      return wb;
                    };

                    try {
                      setExcelBusy(true);
                      const wb = await buildWorkbook();
                      const buf = await wb.xlsx.writeBuffer();
                      saveAs(
                        new Blob([buf], {
                          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                        }),
                        "reporte_parametros_eruptivos.xlsx",
                      );
                    } finally {
                      setExcelBusy(false);
                    }
                  }}
                >
                  <img src={excelIcon} alt="" />
                </button>
                <div className="evt-param-xlsx-card__meta">
                  <div className="evt-param-xlsx-card__title">
                    Archivo completo .xlsx
                  </div>
                  <button
                    type="button"
                    className="evt-param-xlsx-card__link"
                    disabled={excelBusy || (rows || []).length === 0}
                    aria-label="Ver y descargar archivo Excel completo"
                    onClick={() =>
                      document
                        .getElementById("evt-param-excel-trigger")
                        ?.click()
                    }
                  >
                    Ver y descargar
                    <i className="bi bi-box-arrow-up-right" aria-hidden />
                  </button>
                </div>
              </div>

              <div className="evt-param-filebox__divider" aria-hidden="true" />

              <div className="evt-param-filebox__note" role="status">
                <i className="bi bi-check-circle-fill" aria-hidden="true" />
                <span>
                  Este archivo contiene información detallada (parámetros,
                  fuentes y métricas completas) que no se muestra en esta tabla.
                </span>
              </div>
            </div>
          </div>

          {filtered.length > 0 ? (
            <>
              <div className="evt-param-col-trigger-row">
                <button
                  type="button"
                  className="evt-param-col-trigger"
                  onClick={() => setColPanelOpen((open) => !open)}
                  aria-expanded={colPanelOpen}
                  aria-controls="evt-param-col-panel"
                  aria-haspopup={colPanelMobile ? "dialog" : "true"}
                  aria-label={`Personalizar columnas (${visibleColCount} de ${MAX_PARAM_VISIBLE_COLUMNS} visibles)`}
                  data-sgc-tooltip={`${visibleColCount} de ${MAX_PARAM_VISIBLE_COLUMNS} columnas visibles`}
                >
                  <span className="evt-param-col-trigger__start" aria-hidden>
                    <i className="bi bi-gear" />
                  </span>
                  <span className="evt-param-col-trigger__label">
                    Personalizar columnas
                  </span>
                  <span className="evt-param-col-trigger__end" aria-hidden>
                    <i
                      className={
                        colPanelOpen ? "bi bi-chevron-up" : "bi bi-chevron-down"
                      }
                    />
                  </span>
                </button>
              </div>

              {showColPanelAside ? (
                <aside
                  id="evt-param-col-panel"
                  className="evt-param-col-panel evt-param-col-panel--aside"
                  role="region"
                  aria-labelledby="evt-param-col-panel-title"
                >
                  {colPickerPanelBody}
                </aside>
              ) : null}

              <div className="tabla-container">
                <table className="tabla-documentos tabla-documentos--parametros">
                  <colgroup>
                    {cols.map((c) => {
                      const layoutCls = paramColLayoutClass(c);
                      if (c.width)
                        return (
                          <col
                            key={c.key}
                            className={layoutCls || undefined}
                            style={{ width: `${c.width}px` }}
                          />
                        );
                      if (c.widthPct)
                        return (
                          <col
                            key={c.key}
                            className={layoutCls || undefined}
                            style={{ width: `${c.widthPct}%` }}
                          />
                        );
                      return (
                        <col key={c.key} className={layoutCls || undefined} />
                      );
                    })}
                  </colgroup>
                  <thead>
                    <tr>
                      {cols.map((c) => (
                        <th
                          key={c.key}
                          className={
                            [
                              c.isImages ? "evt-th-is-images" : "",
                              paramColLayoutClass(c),
                            ]
                              .filter(Boolean)
                              .join(" ") || undefined
                          }
                          style={
                            c.isImages ? { textAlign: "center" } : undefined
                          }
                        >
                          <div className="th-content">
                            {c.isImages ? (
                              <span
                                className="evt-th-img-col"
                                data-sgc-tooltip="Imágenes"
                                aria-label="Imágenes"
                              >
                                <i className="bi bi-image" aria-hidden />
                              </span>
                            ) : (
                              <span>{c.label}</span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((p, idx) => {
                      const detalle = p?.detalle ?? {};
                      const imagenes = Array.isArray(p?.imagenes)
                        ? p.imagenes
                        : [];
                      return (
                        <tr key={p?.id ?? idx}>
                          {cols.map((c) => {
                            if (c.key === "__index") {
                              return (
                                <td
                                  key={c.key}
                                  className={[
                                    "evt-td-center evt-param-col-idx",
                                    paramColLayoutClass(c),
                                  ]
                                    .filter(Boolean)
                                    .join(" ")}
                                  data-label="#"
                                >
                                  {idx + 1}
                                </td>
                              );
                            }
                            if (c.key === "__fuente") {
                              return (
                                <td
                                  key={c.key}
                                  className={[
                                    "evt-td-center evt-param-col-fuente",
                                    paramColLayoutClass(c),
                                  ]
                                    .filter(Boolean)
                                    .join(" ")}
                                  data-label="Fuente"
                                >
                                  {p?.referencia_bibliografica ?? "—"}
                                </td>
                              );
                            }
                            if (c.key === "__reporte") {
                              const reporteRaw =
                                detalle?.reporte_bibliografico ?? "";
                              const reporteText = String(
                                reporteRaw ?? "",
                              ).trim();
                              return (
                                <td
                                  key={c.key}
                                  className={[
                                    "evt-td-justify",
                                    paramColLayoutClass(c),
                                  ]
                                    .filter(Boolean)
                                    .join(" ")}
                                  data-label="Reporte bibliográfico"
                                >
                                  {reporteText ? (
                                    <div
                                      className="rich-text evt-html evt-html--tight"
                                      dangerouslySetInnerHTML={{
                                        __html:
                                          normalizeAdminRichText(reporteText),
                                      }}
                                    />
                                  ) : (
                                    "—"
                                  )}
                                </td>
                              );
                            }
                            if (c.key === "__imagenes") {
                              return (
                                <td
                                  key={c.key}
                                  className={[
                                    "evt-td-center evt-param-col-img",
                                    paramColLayoutClass(c),
                                  ]
                                    .filter(Boolean)
                                    .join(" ")}
                                  data-label="Imágenes"
                                >
                                  {imagenes.length > 0 ? (
                                    <button
                                      type="button"
                                      className="evt-img-btn evt-img-btn--param"
                                      onClick={() => openGallery(imagenes)}
                                      aria-label="Ver imágenes"
                                      data-sgc-tooltip="Ver imágenes"
                                    >
                                      <i className="bi bi-image" aria-hidden />
                                    </button>
                                  ) : null}
                                </td>
                              );
                            }
                            const val = detalle?.[c.key];
                            const rawText =
                              val != null && String(val).trim() !== ""
                                ? String(val)
                                : "—";
                            const text =
                              rawText !== "—" &&
                              c.key === "municipios_afectados"
                                ? formatLugaresColombiaDisplay(rawText) ||
                                  rawText
                                : rawText;
                            const cls = [
                              paramColLayoutClass(c),
                              c.align === "justify"
                                ? "evt-td-justify"
                                : c.align === "center"
                                  ? "evt-td-center"
                                  : "",
                            ]
                              .filter(Boolean)
                              .join(" ");
                            return (
                              <td
                                key={c.key}
                                className={cls}
                                data-label={c.label}
                              >
                                {text === "—" ? (
                                  "—"
                                ) : shouldRenderRich(val) ? (
                                  <div
                                    className="rich-text evt-html evt-html--tight"
                                    dangerouslySetInnerHTML={{
                                      __html: normalizeAdminRichText(text),
                                    }}
                                  />
                                ) : (
                                  text
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="evt-empty">
              No hay datos para mostrar en {tipoSel}.
            </div>
          )}
        </section>
      </div>

      {colPanelOpen && colPanelMobile && filtered.length > 0 ? (
        <div
          className="evt-modal-overlay evt-param-col-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="evt-param-col-panel-title"
          onClick={closeColPanel}
        >
          <aside
            id="evt-param-col-panel"
            className="evt-param-col-panel evt-param-col-panel--modal"
            onClick={(e) => e.stopPropagation()}
          >
            {colPickerPanelBody}
          </aside>
        </div>
      ) : null}

      <EvtImagenesGaleriaModal
        open={modalOpen}
        images={modalImages}
        index={modalIndex}
        onClose={closeGallery}
        onPrev={prevImg}
        onNext={nextImg}
        onSelectIndex={setModalIndex}
      />
    </>
  );
}
